import { Component, Input, OnInit, inject, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { AmplifyModelService } from '../../services/amplify-model-service.service';
import {
  ServiceAddressService,
  ServiceAddressEntry,
  AggregatedServiceSelection
} from '../../services/service-address.service';
import {
  ServiceAddressPopupComponent,
  ServiceAddressPopupData
} from '../service-address-popup/service-address-popup.component';

@Component({
  selector: 'app-service-address-selector',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSnackBarModule,
    FormsModule
  ],
  templateUrl: './service-address-selector.component.html',
  styleUrl: './service-address-selector.component.css'
})
export class ServiceAddressSelectorComponent implements OnInit {

  @Input({ required: true }) sub: any;
  @Input({ required: true }) customer: any;

  selectionChange = output<{
    serviceAddressIds: string[];
    aggregatedServiceSelections: AggregatedServiceSelection[];
  }>();

  entries: ServiceAddressEntry[] = [];
  serviceOptions: any[] = [];

  private ams = inject(AmplifyModelService);
  private serviceAddressService = inject(ServiceAddressService);
  private dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);

  ngOnInit() {
    this.ams.listItemsForModel('Service').subscribe({
      next: ({ items }: any) => (this.serviceOptions = items),
    });

    this.loadExistingEntries();
  }

  /**
   * Req 9.1/9.2: hydrates `entries` from any SubServiceAddress records
   * already linked to `sub` - the edit case, e.g. reopening the Subscription
   * Builder for a Sub that already has ServiceAddress records attached - and,
   * for each one, the Service ids currently selected for it via
   * ServiceServiceAddress records (Req 11.1/11.3).
   *
   * Delegates to ServiceAddressService.loadEntriesForSub(), which takes a
   * single synced snapshot rather than staying subscribed to a live
   * observeQuery() Observable - important here because a later create (e.g.
   * adding a ServiceAddress via the popup) must not cause this initial
   * load's subscription to re-fire and overwrite entries already pushed
   * locally with a stale/not-yet-resolved `serviceAddress` relation.
   */
  async loadExistingEntries() {
    if (!this.sub || !this.sub.id) return;
    this.entries = await this.serviceAddressService.loadEntriesForSub(this.sub.id);
  }

  /**
   * Req 10.1/10.2: opens ServiceAddressPopupComponent, passing the current
   * customer and the ids of ServiceAddresses already added to this Sub so
   * the popup's "choose existing" list can filter them out. On close,
   * delegates result handling to handlePopupResult().
   */
  openPopup() {
    const dialogRef = this.dialog.open(ServiceAddressPopupComponent, {
      data: {
        customer: this.customer,
        excludeServiceAddressIds: this.entries.map((e) => e.serviceAddress.id),
      } as ServiceAddressPopupData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.handlePopupResult(result);
    });
  }

  /**
   * Req 9.2/10.3/10.4/10.5/10.6: handles the popup's close result - either
   * `{ serviceAddress, isNew }` (selected or newly-created) or `undefined`
   * (cancelled). Delegates all relationship-management to
   * ServiceAddressService.addServiceAddressToSub() rather than calling
   * AmplifyModelService.createRelationship() directly, then pushes the new
   * ServiceAddressEntry and emits the updated selection.
   */
  async handlePopupResult(result: { serviceAddress: any; isNew: boolean } | undefined) {
    if (!result) return; // cancelled

    const { serviceAddress, isNew } = result;

    const { subServiceAddress } = await this.serviceAddressService.addServiceAddressToSub(
      this.sub, this.customer, serviceAddress, isNew
    );

    this.entries.push({ serviceAddress, subServiceAddress, selectedServiceIds: [] });
    this.emitSelectionChange();
  }

  /**
   * Req 9.4/11.2: delegates to
   * ServiceAddressService.removeServiceAddressFromSub(), then removes the
   * entry from this.entries and emits the updated selection.
   */
  async removeEntry(entry: ServiceAddressEntry) {
    await this.serviceAddressService.removeServiceAddressFromSub(this.sub, entry);

    const index = this.entries.indexOf(entry);
    if (index > -1) {
      this.entries.splice(index, 1);
    }

    this.emitSelectionChange();
  }

  /**
   * Req 9.3/11.1/11.2/11.3: delegates to
   * ServiceAddressService.updateServiceSelectionForEntry(), then updates
   * entry.selectedServiceIds and emits the updated selection.
   */
  /**
   * Template helper: mat-selection-list's (selectionChange) hands back a
   * MatSelectionListChange whose selectedOptions we need to map to plain
   * Service id values - templates can't use arrow functions, so that mapping
   * happens here instead of inline in the template.
   */
  onServiceSelectionListChange(entry: ServiceAddressEntry, event: any) {
    const newSelectedServiceIds = event.source.selectedOptions.selected.map((o: any) => o.value);
    this.onServiceSelectionChange(entry, newSelectedServiceIds);
  }

  async onServiceSelectionChange(entry: ServiceAddressEntry, newSelectedServiceIds: string[]) {
    await this.serviceAddressService.updateServiceSelectionForEntry(entry, newSelectedServiceIds);

    entry.selectedServiceIds = newSelectedServiceIds;

    this.emitSelectionChange();
  }

  /**
   * Req 9.2/12.1: delegates to
   * ServiceAddressService.buildAggregatedSelections() and emits the result
   * together with the list of linked ServiceAddress ids.
   */
  private emitSelectionChange() {
    const serviceAddressIds = this.entries.map((e) => e.serviceAddress.id);
    const aggregatedServiceSelections = this.serviceAddressService.buildAggregatedSelections(
      this.entries,
      this.serviceOptions
    );

    this.selectionChange.emit({ serviceAddressIds, aggregatedServiceSelections });
  }
}
